// Game modes + capability flags (ARCH D8/D16, PLR). PURE (ENG-1): the mode is a
// label; `modeCaps` resolves it to the behaviour switches every system reads
// (flight, instant break, invulnerability, infinite items, hunger). Hardcore is a
// world flag layered on top of `survival` (§3.6 `worlds.gameMode`/`difficulty`),
// not a fifth mode.
export const GAME_MODES = ['survival', 'creative', 'adventure', 'spectator'] as const;
export type GameMode = (typeof GAME_MODES)[number];

export function isGameMode(s: string): s is GameMode {
  return (GAME_MODES as readonly string[]).includes(s);
}

export interface ModeCaps {
  /** Free vertical flight (double-space toggles; creative/spectator). */
  readonly fly: boolean;
  /** Every block breaks in one hit, no tool tier gate (creative). */
  readonly instantBreak: boolean;
  /** Immune to all damage sources (creative/spectator). */
  readonly invulnerable: boolean;
  /** Placing/using an item never decrements the stack (creative infinite). */
  readonly infiniteResources: boolean;
  /** May place and break blocks (survival/creative; adventure/spectator can't). */
  readonly canBuild: boolean;
  /** Ticks hunger/exhaustion and can starve (survival/adventure). */
  readonly survivalHunger: boolean;
  /** Passes through blocks and entities, no collision (spectator). */
  readonly noClip: boolean;
  /** Can attack / be targeted / interact with entities (all but spectator). */
  readonly interactsEntities: boolean;
  /** Drops carried items on death (survival/adventure, unless keepInventory). */
  readonly dropsOnDeath: boolean;
}

const CAPS: Readonly<Record<GameMode, ModeCaps>> = {
  survival: {
    fly: false, instantBreak: false, invulnerable: false, infiniteResources: false,
    canBuild: true, survivalHunger: true, noClip: false, interactsEntities: true, dropsOnDeath: true,
  },
  creative: {
    fly: true, instantBreak: true, invulnerable: true, infiniteResources: true,
    canBuild: true, survivalHunger: false, noClip: false, interactsEntities: true, dropsOnDeath: false,
  },
  adventure: {
    fly: false, instantBreak: false, invulnerable: false, infiniteResources: false,
    canBuild: false, survivalHunger: true, noClip: false, interactsEntities: true, dropsOnDeath: true,
  },
  spectator: {
    fly: true, instantBreak: false, invulnerable: true, infiniteResources: false,
    canBuild: false, survivalHunger: false, noClip: true, interactsEntities: false, dropsOnDeath: false,
  },
};

export function modeCaps(mode: GameMode): ModeCaps {
  return CAPS[mode];
}
